import { useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, MailCheck } from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { confirmDialog } from "../../lib/confirm";
import { BRAND_COLORS } from "../../lib/bookingStatus";
import { deDateMediumTime } from "../../lib/dateFormat";

/**
 * Admin-Karte: persönliche Einladungs-Mail mit Login-Link an einen Bestandskunden.
 * Backend blockt den Versand (409), solange FRONTEND_URL auf die Vorschau zeigt.
 */
export function CustomerInviteCard({ customer, onSent }) {
  const [busy, setBusy] = useState(false);
  const sentAt = customer?.invite_sent_at;

  const send = async () => {
    const ok = await confirmDialog({
      title: "Einladungs-Mail senden?",
      description: `Sendet eine persönliche Einladung mit Login-Link an ${customer.email}.`,
      confirmText: "Jetzt senden",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/customers/${customer.id}/send-invite`, {});
      toast.success(`Einladung an ${data.email} gesendet.`);
      onSent?.(data);
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || err.message, { duration: 8000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white border border-navy/10 p-5 md:p-6 mb-6" data-testid="customer-invite-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="label-caps mb-1" style={{ color: BRAND_COLORS.gold }}>Live-Start</div>
          <h3 className="text-navy text-lg mb-1">Persönliche Einladung zur Plattform</h3>
          <p className="text-[12.5px] text-navy-dark/60 leading-relaxed max-w-xl">
            Schickt {customer.name || "dem Kunden"} eine Willkommens-Mail mit dem Login-Link der Produktions-Umgebung
            und einer kurzen Anleitung zum passwortlosen Zugangscode.
          </p>
          <div className="mt-2 text-[12px]" data-testid="customer-invite-status">
            {sentAt ? (
              <span className="inline-flex items-center gap-1.5 text-navy-dark/70">
                <MailCheck className="w-3.5 h-3.5" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.8} />
                Zuletzt gesendet am {deDateMediumTime(sentAt)}{customer.invite_sent_by ? ` · von ${customer.invite_sent_by}` : ""}
              </span>
            ) : (
              <span className="text-navy-dark/50">Noch keine Einladung gesendet.</span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={send}
          disabled={busy}
          data-testid="customer-invite-send-btn"
          className="inline-flex items-center gap-2 text-ivory px-4 py-2 text-[11px] tracking-[0.15em] uppercase hover:opacity-90 disabled:opacity-50"
          style={{ background: BRAND_COLORS.navy }}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" strokeWidth={1.6} />}
          {sentAt ? "Erneut senden" : "Einladung senden"}
        </button>
      </div>
    </section>
  );
}
